import type { CssGeneratorContext } from '../css-generator.model';

export const styles = ({ dt }: CssGeneratorContext) => `
.inspection-step-table-wrapper {
  overflow-x: ${dt('inspectionTables.wrapper.overflowX')};
  overflow-y: ${dt('inspectionTables.wrapper.overflowY')};
  scrollbar-gutter: ${dt('inspectionTables.wrapper.scrollbarGutter')};
  width: 100%;
}

table.inspection-step-table {
  width: 100%;
  min-width: ${dt('inspectionTables.table.minWidth')};
  table-layout: fixed;
  border-collapse: collapse;
  border: none;
}

table.inspection-step-table thead th {
  background: ${dt('inspectionTables.table.theadBackground')};
  color: ${dt('inspectionTables.table.theadColor')};
  font-size: ${dt('inspectionTables.table.thFontSize')};
  font-weight: ${dt('inspectionTables.table.thFontWeight')};
  line-height: ${dt('inspectionTables.table.thLineHeight')};
  padding: ${dt('inspectionTables.table.thPadding')};
  text-align: left;
  vertical-align: top;
  border: 0;
  border-bottom: ${dt('inspectionTables.table.theadBorderBottomWidth')} solid ${dt(
		'inspectionTables.table.theadBorderBottomColor'
	)};
}

app-detected-deficiencies-section table.inspection-step-table thead th:nth-child(1) {
  width: ${dt('inspectionTables.table.colWidths.createdDate')};
}
app-detected-deficiencies-section table.inspection-step-table thead th:nth-child(2) {
  width: ${dt('inspectionTables.table.colWidths.activities')};
}
app-detected-deficiencies-section table.inspection-step-table thead th:nth-child(3) {
  width: ${dt('inspectionTables.table.colWidths.deficiencies')};
}
app-detected-deficiencies-section table.inspection-step-table thead th:nth-child(4) {
  width: ${dt('inspectionTables.table.colWidths.directions')};
}
app-detected-deficiencies-section table.inspection-step-table thead th:nth-child(5) {
  width: ${dt('inspectionTables.table.colWidths.deadline')};
}

app-previous-deficiencies-section table.inspection-step-table thead th:nth-child(1) {
  width: ${dt('inspectionTables.table.colWidthsPrevious.createdDate')};
}
app-previous-deficiencies-section table.inspection-step-table thead th:nth-child(2) {
  width: ${dt('inspectionTables.table.colWidthsPrevious.deficiencies')};
}
app-previous-deficiencies-section table.inspection-step-table thead th:nth-child(3) {
  width: ${dt('inspectionTables.table.colWidthsPrevious.directions')};
}
app-previous-deficiencies-section table.inspection-step-table thead th:nth-child(4) {
  width: ${dt('inspectionTables.table.colWidthsPrevious.status')};
}
app-previous-deficiencies-section table.inspection-step-table thead th:nth-child(5) {
  width: ${dt('inspectionTables.table.colWidthsPrevious.deadline')};
}
app-previous-deficiencies-section table.inspection-step-table thead th:nth-child(6) {
  width: ${dt('inspectionTables.table.colWidthsPrevious.actions')};
}

/*
 * Fixed table layout gives the auto deficiency column whatever the fixed columns leave of the table width, and a
 * column min-width is ignored there. The table's own min-width is therefore the fixed columns plus the deficiency
 * minimum, so a narrower wrapper scrolls instead of shrinking the deficiency column below it; the action column only
 * counts while it is rendered.
 */
app-deficiency-implementation-section table.inspection-step-table {
  min-width: calc(
    ${dt('inspectionTables.table.colWidthsImplementation.deficiencyMinWidth')} +
      ${dt('inspectionTables.table.colWidthsImplementation.deadline')} +
      ${dt('inspectionTables.table.colWidthsImplementation.status')} +
      ${dt('inspectionTables.table.colWidthsImplementation.extended')}
  );
}
app-deficiency-implementation-section table.inspection-step-table:has(thead th:nth-child(5)) {
  min-width: calc(
    ${dt('inspectionTables.table.colWidthsImplementation.deficiencyMinWidth')} +
      ${dt('inspectionTables.table.colWidthsImplementation.deadline')} +
      ${dt('inspectionTables.table.colWidthsImplementation.status')} +
      ${dt('inspectionTables.table.colWidthsImplementation.extended')} +
      ${dt('inspectionTables.table.colWidthsImplementation.action')}
  );
}
app-deficiency-implementation-section table.inspection-step-table thead th:nth-child(1) {
  width: ${dt('inspectionTables.table.colWidthsImplementation.deficiency')};
}
app-deficiency-implementation-section table.inspection-step-table thead th:nth-child(2) {
  width: ${dt('inspectionTables.table.colWidthsImplementation.deadline')};
}
app-deficiency-implementation-section table.inspection-step-table thead th:nth-child(3) {
  width: ${dt('inspectionTables.table.colWidthsImplementation.status')};
}
app-deficiency-implementation-section table.inspection-step-table thead th:nth-child(4) {
  width: ${dt('inspectionTables.table.colWidthsImplementation.extended')};
}
app-deficiency-implementation-section table.inspection-step-table thead th:nth-child(5) {
  width: ${dt('inspectionTables.table.colWidthsImplementation.action')};
}

table.inspection-step-table tbody td {
  padding: ${dt('inspectionTables.table.tdPadding')};
  font-size: ${dt('inspectionTables.table.tdFontSize')};
  vertical-align: ${dt('inspectionTables.table.cellVerticalAlign')};
  border-bottom: 1px solid ${dt('inspectionTables.table.cellBorderColor')};
}

table.inspection-step-table tbody tr.inspection-step-table-row--expanded > td {
  background: ${dt('inspectionTables.table.rowExpandedBackground')};
}

table.inspection-step-table tbody td app-date-field,
table.inspection-step-table tbody td app-inspection-activity-select,
table.inspection-step-table tbody td app-multiline-field,
table.inspection-step-table tbody td app-select {
  display: ${dt('inspectionTables.table.cellControlDisplay')};
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

/*
 * Optimus DatePicker + p-motion (Optimus v1): overlay uses appendTo body while open, but the panel is
 * moved back into the host on leave. In overflow-x:auto table wrappers that briefly widens scroll
 * overflow → scrollbar flicker. Fixes:
 * - Panel min-width only applies while the panel is inside the host (not when portaled to body).
 * - p-motion stays a zero-width flex item so the calendar is not laid out as a third flex column
 *   for a frame before inline position:absolute runs (onOverlayBeforeEnter).
 * - contain:layout on the host limits outward width contributions from internal layout passes.
 * - overflow-x:clip + overflow-y:hidden avoids host expanding scroll width; row height is input-only.
 */
table.inspection-step-table tbody td .p-datepicker {
  contain: layout;
  overflow-x: clip;
  overflow-y: hidden;
}

table.inspection-step-table tbody td .p-datepicker .p-datepicker-panel {
  min-width: unset;
}

table.inspection-step-table tbody td .p-datepicker p-motion {
  flex: 0 0 0;
  width: 0;
  min-width: 0;
  overflow: visible;
  align-self: flex-start;
  position: relative;
}

table.inspection-step-table tbody td app-inspection-activity-select .p-multiselect,
table.inspection-step-table tbody td app-multiline-field textarea {
  width: 100%;
  max-width: 100%;
}

.inspection-step-section-actions {
  display: ${dt('inspectionTables.sectionActions.display')};
  flex-wrap: ${dt('inspectionTables.sectionActions.flexWrap')};
  align-items: ${dt('inspectionTables.sectionActions.alignItems')};
  justify-content: ${dt('inspectionTables.sectionActions.justifyContent')};
  gap: ${dt('inspectionTables.sectionActions.gap')};
  margin-top: ${dt('inspectionTables.sectionActions.marginTop')};
}
`;
